/**
 * Text-to-speech hook using edge-tts backend.
 * 
 * Uses the ai-worker TTS service (Microsoft Edge voices) for high-quality
 * Swedish speech. Falls back to Web Speech API if the TTS service is unavailable.
 * 
 * Swedish voices: sv-SE-SofieNeural (default, female), sv-SE-MattiasNeural (male)
 */
import { useState, useCallback, useRef, useEffect } from 'react'

const TTS_API = '/api/tts/synthesize'

export function useTTS() {
  const [speaking, setSpeaking] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const audioRef = useRef(null)
  const abortRef = useRef(null)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.src = ''
      }
      abortRef.current?.abort()
    }
  }, [])

  /**
   * Speak text using edge-tts backend
   * @param {string} text - Text to speak (max 2000 chars)
   * @param {object} options - Optional settings
   * @param {string} options.voice - Voice alias: 'sofie' (default) or 'mattias'
   * @param {string} options.rate - Speech rate: '+10%', '-20%', etc.
   * @param {string} options.pitch - Pitch adjustment: '+5Hz', '-10Hz', etc.
   */
  const speak = useCallback(async (text, options = {}) => {
    if (!text?.trim()) return

    // Cancel any ongoing speech
    cancel()

    setLoading(true)
    setError(null)

    try {
      // Build query params
      const params = new URLSearchParams({ text: text.trim() })
      if (options.voice) params.set('voice', options.voice)
      if (options.rate) params.set('rate', options.rate)
      if (options.pitch) params.set('pitch', options.pitch)

      // Create abort controller for this request
      abortRef.current = new AbortController()

      const response = await fetch(`${TTS_API}?${params}`, {
        signal: abortRef.current.signal,
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        throw new Error(err.error || `TTS failed: ${response.status}`)
      }

      // Get audio blob and play
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)

      // Create audio element if needed
      if (!audioRef.current) {
        audioRef.current = new Audio()
      }

      const audio = audioRef.current
      audio.src = url

      // Set up event handlers
      audio.onplay = () => {
        setLoading(false)
        setSpeaking(true)
      }
      audio.onended = () => {
        setSpeaking(false)
        URL.revokeObjectURL(url)
      }
      audio.onerror = () => {
        setSpeaking(false)
        setLoading(false)
        URL.revokeObjectURL(url)
        setError('Kunde inte spela upp ljudet')
      }

      await audio.play()
    } catch (err) {
      setLoading(false)
      if (err.name === 'AbortError') return // Cancelled, not an error

      console.warn('[useTTS] Edge TTS failed, falling back to Web Speech API:', err.message)
      setError(err.message)

      // Fallback to Web Speech API
      fallbackSpeak(text)
    }
  }, [])

  /**
   * Fallback to Web Speech API when edge-tts is unavailable
   */
  const fallbackSpeak = useCallback((text) => {
    if (!('speechSynthesis' in window)) {
      setError('Talsyntes stöds inte i denna webbläsare')
      return
    }

    window.speechSynthesis.cancel()

    const utterance = new SpeechSynthesisUtterance(text)
    utterance.lang = 'sv-SE'
    utterance.rate = 0.85
    utterance.pitch = 1.05

    // Find Swedish voice
    const voices = window.speechSynthesis.getVoices()
    const svVoice = voices.find(v =>
      v.lang === 'sv-SE' ||
      v.lang === 'sv' ||
      v.name.toLowerCase().includes('swedish')
    )
    if (svVoice) utterance.voice = svVoice

    utterance.onstart = () => setSpeaking(true)
    utterance.onend = () => setSpeaking(false)
    utterance.onerror = () => setSpeaking(false)

    window.speechSynthesis.speak(utterance)
  }, [])

  /**
   * Cancel ongoing speech
   */
  const cancel = useCallback(() => {
    // Cancel fetch request
    abortRef.current?.abort()

    // Stop audio playback
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }

    // Stop Web Speech API fallback
    window.speechSynthesis?.cancel()

    setSpeaking(false)
    setLoading(false)
  }, [])

  // Pause/resume on visibility change
  useEffect(() => {
    function onVisibility() {
      if (!audioRef.current) return
      if (document.hidden) {
        audioRef.current.pause()
      } else if (speaking) {
        audioRef.current.play().catch(() => {})
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [speaking])

  return {
    speak,
    cancel,
    speaking,
    loading,
    error,
    // Always supported — has Web Speech API fallback
    supported: true,
  }
}
